/**
 * Infrastructure: API Company Repository
 *
 * Implementation of CompanyRepository that fetches data from the backend API.
 * Handles the translation between API response format and domain entities.
 *
 * Key responsibilities:
 * - Fetching company data via query dispatcher
 * - Mapping API response structure to domain model
 * - Handling missing/null data gracefully
 *
 * @module analitic_accounting/infra/ApiCompanyRepository
 */

import { CommandDispatcher } from "@/modules/shared/domain/CommandDispatcher";
import { QueryDispatcher } from "@/modules/shared/domain/QueryDispatcher";
import { query_dispatcher_get, sync_command_dispatcher, async_command_dispatcher } from "@/api/client";
import { CompanyRepository } from "../domain/CompanyRepository";
import { Company, AnalyticPlan, Level, Account, AnalyticsPlanConfiguration, AnalyticPlanReference, ThirdType, AnalyticReferenceType } from "../domain/Company";
import { Third } from "../domain/Third";

/**
 * Recursively converts an object to a plain dictionary.
 * Handles nested objects but preserves arrays.
 *
 * @param obj - Object to convert
 * @returns Plain dictionary representation
 * @deprecated This function is currently unused (commented out in domain_to_api)
 */
function objectToDict(obj: any): Record<string, any> {
    const dict: Record<string, any> = {};

    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            const value = obj[key];
            if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                dict[key] = objectToDict(value);
            } else {
                dict[key] = value;
            }
        }
    }

    return dict;
}

/**
 * API-based implementation of CompanyRepository.
 * Fetches company analytic accounting configuration from the backend API.
 */
export class ApiCompanyRepository implements CompanyRepository {

    /** Command dispatcher for write operations (currently unused) */
    command_dispatcher: CommandDispatcher

    /** Query dispatcher for read operations */
    query_dispatcher: QueryDispatcher

    /**
     * Creates the repository with configured dispatchers
     */
    constructor() {
        this.command_dispatcher = async_command_dispatcher
        this.query_dispatcher = query_dispatcher_get
    }

    /**
     * Converts API response format to domain entities.
     * Handles mapping of:
     * - Plans and their nested levels
     * - Account-specific plan assignments
     * - Configuration flags
     * - Percentage fields (API: "percentage", Domain: "percent")
     *
     * Defensive programming: Returns empty arrays if data is missing/null
     *
     * @param company - Raw company data from API
     * @returns Company domain entity
     */
    api_to_domain(company) {
        let c = new Company(company.id);

        function mapLevel(level: any): Level {
            return {
                code: level.code,
                name: level.name,
                percent: level.percentage,
                ui_visible: level.ui_visible,
                levels: Array.isArray(level.levels) ? level.levels.map(mapLevel) : []
            };
        }

        function mapPlan(plan: any): AnalyticPlan {
            return {
                code: plan.code,
                name: plan.name,
                levels: Array.isArray(plan.levels) ? plan.levels.map(mapLevel) : []
            };
        }
        
        if (!Array.isArray(company.plans)) {
            company.plans = [];
        } else {
            c.plans = company.plans.map((plan: any) => {
                return new AnalyticPlan(
                    plan.code,
                    plan.name,
                    Array.isArray(plan.levels) ? plan.levels.map(mapLevel) : []
                );
            });
        }

        if (!Array.isArray(company.accounts_plans)) {
            company.accounts_plans = [];
        } else {
            c.accounts_plans = company.accounts_plans.map((account_plans: any) => {
                return new Account(
                    account_plans.code,
                    Array.isArray(account_plans.analytic_plans) ? account_plans.analytic_plans.map(mapPlan) : []
                );
            });
        }

        if (!company.analytics_plan_configuration){
            company.analytics_plan_configuration = new AnalyticsPlanConfiguration();
        } else {
            c.analytics_plan_configuration = new AnalyticsPlanConfiguration({
                is_analytic_plan: company.analytics_plan_configuration.is_analytic_plan,
                is_allow_analytics_plans_all_accounts: company.analytics_plan_configuration.is_allow_analytics_plans_all_accounts,
                is_manual_input_distribution: company.analytics_plan_configuration.is_manual_input_distribution,
                is_linear_automatic_distribution: company.analytics_plan_configuration.is_linear_automatic_distribution,
                is_automatic_percentage_distribution: company.analytics_plan_configuration.is_automatic_percentage_distribution,
                is_single_analytic_level: company.analytics_plan_configuration.is_single_analytic_level,
                is_only_defined_levels: company.analytics_plan_configuration.is_only_defined_levels,
            });
        }

        // Map analytic_plan_references from API format
        if (!Array.isArray(company.analytic_plan_references)) {
            c.analytic_plan_references = [];
        } else {
            c.analytic_plan_references = company.analytic_plan_references.map((ref: any): AnalyticPlanReference => {
                return {
                    id: ref.id,
                    account_rule_type: ref.account_rule_type as AnalyticReferenceType,
                    analytic_plans: Array.isArray(ref.analytic_plans) ? ref.analytic_plans.map(mapPlan) : [],
                    account_rules: Array.isArray(ref.account_rules) ? ref.account_rules : [],
                    thirds: Array.isArray(ref.thirds) ? ref.thirds.map((third: any): Third => ({
                        id: third.id,
                        name: third.name,
                        account: third.account,
                        expens_account: third.expens_account,
                        income_account: third.income_account,
                        third_type: third.third_type as ThirdType
                    })) : [],
                    references: Array.isArray(ref.references) ? ref.references : [],
                    apply_to_suppliers: ref.apply_to_suppliers !== undefined ? ref.apply_to_suppliers : true,
                    apply_to_customers: ref.apply_to_customers !== undefined ? ref.apply_to_customers : true
                };
            });
        }

        return c;
    }

    /**
     * Converts domain Company to API format (currently commented out/unused).
     * Would be used for save/update operations if implemented.
     *
     * @param company - Domain Company entity
     * @returns API-formatted object
     * @deprecated Currently not in use
     */
    // domain_to_api(company: Company) {
    //     let res = JSON.parse(JSON.stringify(objectToDict(company)));
    //     return res
    // }

    /**
     * Fetches a company's analytic accounting configuration from the API.
     * Dispatches a query to "get.company.information" endpoint.
     *
     * @param company_id - The company ID to fetch
     * @returns Promise resolving to Company domain entity (empty if not found)
     */
    async find_by_id(company_id:number): Promise<Company> {
        let company = await this.query_dispatcher.dispatch("get.company.information", {company_id: company_id});
        if (!company) {
            return new Company(company_id);
        }

        return this.api_to_domain(company);
    }

    /**
     * Saves analytic plan references for a company.
     * Sends a command to set.analytic_plan_references endpoint.
     *
     * @param user_id - The user ID making the request
     * @param company_id - The company ID to update
     * @param analytic_plan_references - Array of analytic plan references to save
     * @returns Promise that resolves when the save is complete
     */
    async save_analytic_plan_references(
        user_id: number,
        company_id: number,
        analytic_plan_references: AnalyticPlanReference[]
    ): Promise<void> {
        // Convert domain format to API format (snake_case)
        const references_primitive = analytic_plan_references.map(ref => ({
            id: ref.id,
            account_rule_type: ref.account_rule_type,
            analytic_plans: ref.analytic_plans.map(plan => ({
                code: plan.code,
                name: plan.name,
                levels: this.map_levels_to_api(plan.levels)
            })),
            account_rules: ref.account_rules,
            thirds: ref.thirds.map(third => ({
                id: third.id,
                name: third.name,
                account: third.account,
                expens_account: third.expens_account,
                income_account: third.income_account,
                third_type: third.third_type
            })),
            references: ref.references,
            apply_to_suppliers: ref.apply_to_suppliers,
            apply_to_customers: ref.apply_to_customers
        }));

        await sync_command_dispatcher.dispatch('set.analytic_plan_references', {
            user_id: user_id,
            company_id: company_id,
            analytic_plan_references: references_primitive
        });
    }

    /**
     * Helper method to recursively map Level objects to API format
     * @param levels - Array of Level objects
     * @returns Array of plain objects suitable for API
     */
    private map_levels_to_api(levels: Level[]): any[] {
        return levels.map(level => ({
            code: level.code,
            name: level.name,
            percentage: level.percent,
            ui_visible: level.ui_visible,
            levels: this.map_levels_to_api(level.levels)
        }));
    }

}